<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import {
  SideMenu as SideMenuSDL,
  type IconName,
  type BadgeType,
} from '@easybell/caramel-vue3'
import {
  isProduction,
  getBranchName,
  isLocal,
  getCommitHash,
} from '@/utils/environment'
import { useCoreStore } from '@/stores/core/store'
import { useCtaStore } from '@/stores/cta/store'
import { useFeaturesStore } from '@/stores/features/store'
import { useNavigationStore } from '@/stores/navigation/store'
import { useUiStore } from '@/stores/ui/store'
import { useAuthStore } from '@/stores/auth/store'
import LinkTag from '@/components/LinkTag.vue'
import type { NavigationEntry } from '@/stores/navigation/api'

const i18n = useI18n()

const route = useRoute()

const uiStore = useUiStore()

const authStore = useAuthStore()

const ctaPathRouteNames: Record<string, string[]> = {
  '/uca/users': [
    'uca-users',
    'uca-users-edit',
    'uca-users-new',
    'uca-users-nightswitch',
    'uca-user-profile-step-add',
    'uca-user-profile-step-edit',
    'uca-user-profile-add',
    'uca-user-profile-edit',
    'uca-user-extended-forwarding',
    'uca-user-simple-forwarding',
    'uca-user-call-flow',
    'uca-users-multi-edit',
    'uca-extension-new',
    'uca-extension-edit',
    'uca-profile-addstep',
    'uca-profile-editstep',
    'uca-profile-add',
    'uca-profile-edit',
    'uca-extendedforwaring',
    'uca-simpleforwarding',
    'uca-nightswitch',
    'uca-extension-multiedit',
  ],
  '/uca/resources': [
    'uca-resources',
    'uca-resources-new',
    'uca-resources-edit',
    'uca-assign-devices',
    'uca-resources-nightswitch',
    'uca-resources-callflow',
    'uca-resources-multi-edit',
    'uca-resource-extended-forwarding',
    'uca-resource-simple-forwarding',
    'uca-resource-profile-add',
    'uca-resource-profile-edit',
    'uca-resource-profile-step-add',
    'uca-resource-profile-step-edit',
  ],
  '/uca/devices': [
    'uca-devices',
    'uca-device-add',
    'uca-device-edit',
    'uca-device-multiedit',
    'uca-device-password',
    'uca-device-allowed-ips',
    'uca-device-softkeys',
  ],
  '/uca/groups': ['uca-groups', 'uca-group-add', 'uca-group-edit'],
  '/uca/audio-files': [
    'uca-audio-files',
    'uca-audiofiles-new',
    'uca-audiofiles-record-new',
    'uca-audiofiles-edit',
    'uca-audiofiles-record',
  ],
  '/uca/office-desks': [
    'uca-office-desks',
    'uca-office-desk-add',
    'uca-office-desk-edit',
    'uca-office-desk-multi-edit',
  ],
  '/uca/contacts': ['uca-contacts'],
  '/uca/settings': ['uca-settings'],
}

const ctaStore = useCtaStore()

const coreStore = useCoreStore()

const featuresStore = useFeaturesStore()

const navigationStore = useNavigationStore()

type SideMenuItemBase = {
  id?: string
  label: string
  icon: IconName
  isSelected?: boolean
  hasTopDivider?: boolean
  badge?: { label: string; type?: BadgeType }
  tone?: 'default' | 'danger'
}

type SideMenuItem = SideMenuItemBase & {
  href?: string
  children?: never
}

type SideMenuItemGroup = SideMenuItemBase & {
  href?: never
  children?: SideMenuItem[]
}

type ClickableSideMenuItem = SideMenuItem & {
  onClick?: () => void
}

type ClickableSideMenuEntry =
  | ClickableSideMenuItem
  | (Omit<SideMenuItemGroup, 'children'> & {
      children: ClickableSideMenuEntry[]
      onClick?: never
    })

const getHref = computed(() => (entry: NavigationEntry) => {
  return entry.path ?? (entry.pathKey ? i18n.t(entry.pathKey) : undefined)
})

function parseNavigationEntry(entry: NavigationEntry): ClickableSideMenuEntry {
  const href = getHref.value(entry)

  const routeNames = entry.path ? ctaPathRouteNames[entry.path] : undefined

  const isSelected = routeNames
    ? routeNames.includes(String(route.name))
    : undefined

  return {
    id: entry.label,
    label: i18n.t(entry.label),
    icon: entry.icon,
    isSelected,
    ...(href
      ? { href }
      : { children: entry.children.map(parseNavigationEntry) }),
    hasTopDivider: entry.hasTopDivider ?? undefined,
    badge: entry.badge ?? undefined,
    tone: entry.tone ?? undefined,
  } satisfies ClickableSideMenuEntry
}

const debugButtons = computed((): ClickableSideMenuItem[] => [
  ...Object.entries(ctaStore.ctaData.specialButtons ?? {}).map(
    ([label, href]) =>
      ({
        id: label,
        label,
        href,
        icon: 'Redirect',
      }) satisfies ClickableSideMenuItem
  ),
  {
    id: 'bg-none',
    label: 'Background: none',
    icon: 'Webinars',
    onClick: () => uiStore.setBackground('none'),
  },
  {
    id: 'bg-text-environment',
    label: 'Background: professional-looking test watermark',
    icon: 'Webinars',
    onClick: () => uiStore.setBackground('test-environment'),
  },
  {
    id: 'bg-ping',
    label: 'Background: PING!!',
    icon: 'Webinars',
    onClick: () => uiStore.setBackground('ping'),
  },
  ...(!isLocal()
    ? ([
        {
          id: 'branch',
          label: getBranchName(),
          icon: 'Github',
        },
        {
          id: 'hash',
          label: getCommitHash(),
          icon: 'Github',
        },
      ] satisfies ClickableSideMenuItem[])
    : []),
])

const debugButtonsItems = computed((): ClickableSideMenuEntry[] => [
  {
    id: 'debug',
    icon: 'Bug',
    label: i18n.t('overview.debugMenu'),
    children: debugButtons.value,
  },
])

const topItems = computed((): ClickableSideMenuEntry[] => [
  ...(navigationStore.navigation?.topLinks ?? []).map(parseNavigationEntry),
  ...(debugButtons.value.length && !isProduction()
    ? debugButtonsItems.value
    : []),
  ...(featuresStore.hasGroupsFeature && ctaStore.ctaData.edition === 'pro'
    ? [
        parseNavigationEntry({
          icon: 'FolderGroup',
          label: 'Groups',
          path: '/uca/groups',
          pathKey: null,
          children: [],
        }),
      ]
    : []),
])

const bottomItems = computed((): ClickableSideMenuEntry[] => [
  ...(featuresStore.hasUcaSwitchButton
    ? ([
        {
          id: 'exit-uca',
          label: i18n.t('uca.exit-button'),
          icon: 'ArrowLeftTurn',
          onClick: () => featuresStore.toggleIsUcaMode(),
        },
      ] satisfies ClickableSideMenuItem[])
    : []),
  ...(navigationStore.navigation?.bottomLinks ?? []).map(parseNavigationEntry),
])

const languages = computed(() => [
  {
    label: i18n.t('global.common.language.german'),
    locale: 'de' as const,
    onClick: () => coreStore.changeLanguage('de'),
  },
  {
    label: i18n.t('global.common.language.english'),
    locale: 'en' as const,
    onClick: () => coreStore.changeLanguage('en'),
  },
  {
    label: i18n.t('global.common.language.french'),
    locale: 'fr' as const,
    onClick: () => coreStore.changeLanguage('fr'),
  },
  {
    label: i18n.t('global.common.language.dutch'),
    locale: 'nl' as const,
    onClick: () => coreStore.changeLanguage('nl'),
  },
  ...(!isProduction()
    ? [
        {
          label: 'DEBUG',
          locale: 'debug' as const,
          onClick: () => coreStore.changeLanguage('debug'),
        },
      ]
    : []),
])

const userMenuItems = computed(
  (): ClickableSideMenuEntry[] =>
    navigationStore.navigation?.popoutLinks.map((entry) => {
      switch (entry.label) {
        // Override language selector
        case 'navigation.language.label':
          return {
            id: 'change-language',
            label:
              'Language: ' +
              languages.value.find(
                (language) => language.locale === i18n.locale.value
              )?.label,
            icon: 'LanguageTranslate',
            children: languages.value.map(
              (language) =>
                ({
                  id: `change-language-${language.locale}`,
                  label: language.label,
                  icon: 'LanguageTranslate',
                  isSelected: language.locale === i18n.locale.value,
                  onClick: () => coreStore.changeLanguage(language.locale),
                }) satisfies ClickableSideMenuItem
            ),
          }

        // Override logout button
        case 'navigation.logout.label':
          return {
            id: 'logout',
            label: i18n.t('global.logout'),
            icon: 'Exit',
            href: '/logout',
            tone: 'danger',
          }
      }

      return parseNavigationEntry(entry)
    }) ?? []
)

const userRole = computed(() => {
  if (!authStore?.role) {
    return ''
  }
  return authStore.role.charAt(0).toUpperCase() + authStore.role.slice(1)
})

const userEmail = computed(() => {
  if (authStore.isOfficeDesk) {
    return authStore.email ?? ''
  } else {
    return navigationStore.navigation?.user.email ?? ''
  }
})

const userName = computed(() => {
  if (authStore.isOfficeDesk) {
    return authStore.username?.split('-').slice(1).join('-') ?? ''
  } else {
    return navigationStore.navigation?.user.name ?? ''
  }
})

function onItemClick(id?: string) {
  ;[topItems.value, bottomItems.value, userMenuItems.value]
    .flat()
    .flatMap((el) => el.children ?? [el])
    .find((el) => el.id === id)
    ?.onClick?.()
}
</script>

<template>
  <SideMenuSDL
    :topItems="topItems"
    :bottomItems="bottomItems"
    :userMenuItems="userMenuItems"
    :email="userEmail"
    homeLink="/"
    :userName="userName"
    :customerId="authStore.customer ?? ''"
    :userStatus="userRole"
    :linkTag="LinkTag"
    data-test="side-menu"
    @itemClick="onItemClick"
  />
</template>

<style scoped>
/*
 * The design-system SideMenu renders the profile name inside a `1fr` grid
 * column whose `min-width` defaults to `auto`. A long, unbroken user name
 * forces that column to expand, overflowing the sidebar and preventing the
 * built-in `truncate` from clipping the text. Allowing the column (and the
 * name element itself) to shrink (`min-width: 0`) re-enables truncation.
 */
:deep(
  [data-test='side-menu-profile-userinfo']
    > div:has(> [data-test='side-menu-profile-name'])
) {
  min-width: 0;
}

:deep([data-test='side-menu-profile-name']) {
  min-width: 0;
}
</style>
